import { type FC, type RefObject } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useFeatureFlags } from "@jti/commerce";
import type { ShippingFormFields } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Form, useForm, useTranslations } from "@jti/ui";

import { OrderAttributesSlot } from "@checkout-v2/features";
import { DeliveryInformation } from "@checkout-v2/features/DeliveryInformation";
import { DeliveryAddresses } from "@checkout-v2/features/DeliveryInformation/DeliveryAddresses";
import { telephoneFieldFactory } from "@checkout-v2/formFields";
import { AddressCustomShippingField } from "@checkout-v2/formFields/components/AddressCustomShippingField";
import { AddressHomeDeliveryField } from "@checkout-v2/formFields/components/AddressHomeDeliveryField";
import { AddressInStoreDeliveryField } from "@checkout-v2/formFields/components/AddressInStoreDeliveryField";
import { BillingAddressField } from "@checkout-v2/formFields/components/BillingAddressField";
import { DeliveryOptionsField } from "@checkout-v2/formFields/components/DeliveryOptionsField";
import { DeliveryOptionsFieldV2 } from "@checkout-v2/formFields/components/DeliveryOptionsFieldV2";
import { IsBillingSameAsShippingField } from "@checkout-v2/formFields/components/IsBillingSameAsShippingField";
import { ShippingMethodSelectorField } from "@checkout-v2/formFields/components/ShippingMethodSelectorField";
import { shippingMethodFieldFactory } from "@checkout-v2/formFields/factories/shippingMethodCodeFieldFactory";
import type { SubmitHandler } from "@checkout-v2/forms";
import { BasicContactDetailsSeparator } from "@checkout-v2/forms/ShippingForm/components";
import { BasicContactDetailsSection } from "@checkout-v2/forms/ShippingForm/components/BasicContactDetailsSection/BasicContactDetailsSection";
import { usePreparedDeliveryOptions } from "@checkout-v2/forms/ShippingForm/hooks/usePreparedDeliveryOptions";
import { hasRequiredBillingAddressFields } from "@checkout-v2/forms/ShippingForm/renderer/utils";
import type { ShippingFormFieldsConfig } from "@checkout-v2/forms/ShippingForm/ShippingForm.types";
import { useOrderLimit, useSetBillingAddressOnCart } from "@checkout-v2/shared/hooks";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";
import { hasExternalAddressValidationError } from "@checkout-v2/shared/utils/externalAddressValidationErrorUtils";
import { isDeliveryOptionsSelectionDisabled } from "@checkout-v2/shared/utils/prepareDeliveryOptions";

import styles from "./ShippingFormRenderer.module.scss";

export type ShippingFormRendererProps = {
  formFieldsConfig: ShippingFormFieldsConfig;
  defaultValues: ShippingFormFields;
  billingAddressCardRef?: RefObject<HTMLDivElement | null>;
  onShippingMethodChange?: (newMethodCode: string) => void;
  isShippingAgeVerificationEnabled?: boolean;
  isAgeVerified?: boolean;
  hasCompletedAgeVerification?: boolean;
  isAgeVerificationError?: boolean;
  isAgeVerificationPending?: boolean;
  onVerifyAge?: () => void;
  onSubmit: SubmitHandler<ShippingFormFields>;
};

export const ShippingFormRenderer: FC<ShippingFormRendererProps> = ({
  formFieldsConfig,
  defaultValues,
  billingAddressCardRef,
  onShippingMethodChange,
  isShippingAgeVerificationEnabled = false,
  isAgeVerified = false,
  hasCompletedAgeVerification = false,
  isAgeVerificationError = false,
  isAgeVerificationPending = false,
  onVerifyAge,
  onSubmit,
}) => {
  const config = useConfig();
  const { __ } = useTranslations();
  const checkoutConfiguration = useCheckoutV2Configuration();
  const { isOrderLimitExceeded } = useOrderLimit();
  const { setBillingAddressOnCartResult } = useSetBillingAddressOnCart();

  const form = useForm<ShippingFormFields>({ defaultValues, mode: "onChange" });

  const selectedShippingMethodCode = form.watch(formFieldsConfig.shippingMethodCode.name);
  const isBillingAddressSameAsShippingFieldValue = form.watch(formFieldsConfig.isBillingAddressSameAsShipping.name);
  const firstname = form.watch(formFieldsConfig.firstName.name);
  const lastname = form.watch(formFieldsConfig.lastName.name);
  const telephoneFieldValue = form.watch(formFieldsConfig.telephone.name);

  const telephone = telephoneFieldFactory.toTelephoneString(
    telephoneFieldValue,
    formFieldsConfig.telephone.savedDefaultValue
  );

  const selectedShippingMethodType = shippingMethodFieldFactory.getSelectedShippingMethodType(
    selectedShippingMethodCode,
    checkoutConfiguration
  );
  const isSelectedMethodAgeRestricted =
    isShippingAgeVerificationEnabled &&
    isAgeVerified !== true &&
    formFieldsConfig.shippingMethodCode.options.some(
      (method) => method.value === selectedShippingMethodCode && method.disableForNotAgeVerified
    );
  const hasCompleteDefaultBillingAddress = hasRequiredBillingAddressFields(
    formFieldsConfig.billingAddress.defaultAddress,
    {
      isStreetFieldCombined: config.isStreetFieldCombined,
    }
  );

  const { isSubscription, preparedDeliveryOptions } = usePreparedDeliveryOptions({
    form,
    rawOptions: formFieldsConfig.deliveryOption.options,
    methodType: selectedShippingMethodType,
    selectedMethodCode: selectedShippingMethodCode,
    initialSelectedDeliveryOption: defaultValues.deliveryOption,
    fieldName: formFieldsConfig.deliveryOption.name,
    onChange: formFieldsConfig.deliveryOption.onChange,
  });

  const deliveryOptionFieldConfig = {
    ...formFieldsConfig.deliveryOption,
    isSubscription,
    options: preparedDeliveryOptions,
    selectionDisabled: isDeliveryOptionsSelectionDisabled(preparedDeliveryOptions, isSubscription),
    isHidden: preparedDeliveryOptions.length === 0 && !formFieldsConfig.deliveryOption.isLoading,
  };

  const isExternalAddressValidationBillingError = hasExternalAddressValidationError(
    setBillingAddressOnCartResult.error
  );

  const onBillingAddressTypeChange = (isSameAsShipping: boolean) => {
    if (isSameAsShipping) {
      form.setValue(formFieldsConfig.billingAddress.name, null);
    }
  };

  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const onDeliveryMethodChange = async (newMethodCode: string) => {
    form.setValue(formFieldsConfig.shippingMethodCode.name, newMethodCode);
    onShippingMethodChange?.(newMethodCode);

    await form.trigger(formFieldsConfig.shippingMethodCode.name);
  };

  return (
    <Form form={form} className={styles.form} onSubmit={(data) => onSubmit(data, form)} onInvalid={console.warn}>
      <Card
        title={__("cart.checkout.shipping_contact.title")}
        description={__("cart.checkout.shipping_contact.intro")}
        data-testid="shipping-contact-card"
      >
        <div className={styles.cardContent}>
          <BasicContactDetailsSection formFieldsConfig={formFieldsConfig} defaultValues={defaultValues} />

          {!ffCheckoutV2EnableIteration2 && (
            <>
              <BasicContactDetailsSeparator shippingMethodCode={formFieldsConfig.shippingMethodCode} />

              <ShippingMethodSelectorField
                formFieldsConfig={formFieldsConfig}
                onMethodChange={onShippingMethodChange}
              />
            </>
          )}

          {selectedShippingMethodType === "custom delivery" && !ffCheckoutV2EnableIteration2 && (
            <AddressCustomShippingField
              formFieldsConfig={formFieldsConfig}
              selectedShippingMethodCode={selectedShippingMethodCode}
            />
          )}

          {selectedShippingMethodType === "in-store delivery" && !ffCheckoutV2EnableIteration2 && (
            <AddressInStoreDeliveryField
              formFieldsConfig={formFieldsConfig}
              selectedShippingMethodCode={selectedShippingMethodCode}
            />
          )}
        </div>
      </Card>
      {ffCheckoutV2EnableIteration2 && (
        <DeliveryInformation
          methods={formFieldsConfig.shippingMethodCode.options}
          selectedValue={selectedShippingMethodCode}
          onMethodChange={onDeliveryMethodChange}
          isShippingAgeVerificationEnabled={isShippingAgeVerificationEnabled}
          isAgeVerified={isAgeVerified}
          hasCompletedAgeVerification={hasCompletedAgeVerification}
          isAgeVerificationError={isAgeVerificationError}
          isAgeVerificationPending={isAgeVerificationPending}
          onVerifyAge={onVerifyAge}
          name={formFieldsConfig.shippingMethodCode.name}
        >
          {selectedShippingMethodType === "in-store delivery" && (
            <AddressInStoreDeliveryField
              formFieldsConfig={formFieldsConfig}
              selectedShippingMethodCode={selectedShippingMethodCode}
            />
          )}

          {selectedShippingMethodType === "custom delivery" && (
            <AddressCustomShippingField
              formFieldsConfig={formFieldsConfig}
              selectedShippingMethodCode={selectedShippingMethodCode}
            />
          )}

          <DeliveryAddresses
            formFieldsConfig={formFieldsConfig}
            selectedShippingMethodType={selectedShippingMethodType}
            firstname={firstname}
            lastname={lastname}
            telephone={telephone}
            billingAddressCardRef={billingAddressCardRef}
            showBillingValidationError={isExternalAddressValidationBillingError}
          />
        </DeliveryInformation>
      )}
      {!ffCheckoutV2EnableIteration2 && selectedShippingMethodType === "atHome" && (
        <Card title={__("cart.checkout.shipping_address.title")} data-testid="shipping-address-card">
          <div className={styles.cardContent}>
            <OrderAttributesSlot slotTarget="shippingAddress_before" />

            <AddressHomeDeliveryField
              fieldConfig={formFieldsConfig.addressHomeDelivery}
              telephone={telephone}
              firstname={firstname}
              lastname={lastname}
            />

            <OrderAttributesSlot slotTarget="shippingAddress_after" />
          </div>
        </Card>
      )}
      {!ffCheckoutV2EnableIteration2 && config.isBillingAddressVisibleForCheckout && (
        <div ref={billingAddressCardRef}>
          <Card
            title={__("cart.billing.title")}
            description={__("cart.billing.subtitle")}
            data-testid="billing-address-card"
            variant="level-1"
          >
            <div className={styles.cardContent}>
              {isExternalAddressValidationBillingError && (
                <AlertBanner
                  tone="warning"
                  description={__("cart.checkout.warning.billing_address_validation_description")}
                />
              )}

              <OrderAttributesSlot slotTarget="billingaddress_before" />

              {selectedShippingMethodType === "atHome" && (
                <IsBillingSameAsShippingField
                  fieldConfig={formFieldsConfig.isBillingAddressSameAsShipping}
                  onChangeCallback={onBillingAddressTypeChange}
                  billingConfig={formFieldsConfig.billingAddress}
                />
              )}

              {selectedShippingMethodType === "atHome" && !isBillingAddressSameAsShippingFieldValue && (
                <BillingAddressField
                  key={selectedShippingMethodType}
                  initialMode="custom"
                  fieldConfig={formFieldsConfig.billingAddress}
                />
              )}

              {selectedShippingMethodType !== "atHome" && (
                <BillingAddressField
                  key={selectedShippingMethodType}
                  fieldConfig={formFieldsConfig.billingAddress}
                  initialMode={hasCompleteDefaultBillingAddress ? "view" : "custom"}
                  address={formFieldsConfig.billingAddress.defaultAddress}
                />
              )}

              {!isBillingAddressSameAsShippingFieldValue && <OrderAttributesSlot slotTarget="billingaddress_after" />}
            </div>
          </Card>
        </div>
      )}
      {deliveryOptionFieldConfig.isHidden ? (
        <div data-testid="delivery-options-card-no-options">
          <Card title={__("cart.checkout.shipping_options.title")}>
            <AlertBanner tone="info" description={__("cart.checkout.shipping_options.no_options")} />
          </Card>
        </div>
      ) : ffCheckoutV2EnableIteration2 ? (
        <DeliveryOptionsFieldV2 fieldConfig={deliveryOptionFieldConfig} />
      ) : (
        <DeliveryOptionsField fieldConfig={deliveryOptionFieldConfig} />
      )}
      <Button
        type="submit"
        pending={form.formState.isSubmitting}
        disabled={isOrderLimitExceeded || isSelectedMethodAgeRestricted}
      >
        {__("cart.checkout.shipping_form.button.submit_form")}
      </Button>
    </Form>
  );
};
